import i18next from "i18next"
import { MutationCache, QueryClient, QueryClientProvider } from "react-query"
import { ReactQueryDevtools } from "react-query/devtools"

import { toast, ToastBody, ToastTitle } from "@atoms"
import api from "services/api"
import { dispatchLoggedOutEvent } from "./auth"
import dispatchMaintenanceMode from "./maintenance"
import {
  dispatchMaintenanceEvent,
  dispatchDevelopmentEvent,
  dispatchStagingEvent,
  dispatchProductionEvent,
  dispatchLocalEvent,
} from "./banner/utils"

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess(_, __, ___, mutation) {
      const meta = mutation.options.meta
      let message: string = ""
      switch (true) {
        case !!meta?.message:
          message = meta?.message as string
          break
        case !!meta?.entity:
          message = i18next.t(`mutations.success.${meta?.type}`, {
            entity: meta?.entity,
            context: meta?.context,
            count: meta?.count as number,
          })
          break
      }

      if (message) {
        toast.success(
          <ToastTitle>{message}</ToastTitle>,
          (meta?.toast as any) || {}
        )
      }
    },
    onError(error: any, __, ___, mutation) {
      const meta = mutation.options.meta

      switch (true) {
        case meta?.handleError !== false && error.response.status === 400:
          toast.error(
            <>
              <ToastTitle>{i18next.t("error")}</ToastTitle>
              <ToastBody>{error.response.data.message}</ToastBody>
            </>,
            (meta?.toast as any) || { duration: 12000 }
          )
          break
        case meta?.handleError !== false && error.response.status !== 422:
          toast.error(
            <ToastTitle>{i18next.t("unexpectedError")}</ToastTitle>,
            (meta?.toast as any) || {}
          )
          break
        case error.response.status === 401:
          dispatchLoggedOutEvent()
          break
        case error.response.status === 503:
          dispatchMaintenanceMode()
      }
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: ({ signal, queryKey }) => {
        const params = queryKey
          .filter((key) => typeof key === "object")
          .reduce((acc: any, current: any) => ({ ...acc, ...current }), {})
        return api
          .get(
            queryKey
              .filter(
                (key) => typeof key === "string" || typeof key === "number"
              )
              .join("/"),
            {
              params,
              signal,
            }
          )
          .then((res) => {
            if (res.headers["api-environment"]) {
              if (res.headers["maintenance-mode"] === "1") {
                dispatchMaintenanceEvent()
              } else {
                const dispatchBannerEvent = () => {
                  switch (res.headers["api-environment"]) {
                    case "production":
                      return dispatchProductionEvent()
                    case "staging":
                      return dispatchStagingEvent()
                    case "development":
                      return dispatchDevelopmentEvent()
                    case "local":
                      return dispatchLocalEvent()
                  }
                }
                dispatchBannerEvent()
              }
            }
            return res.data?.data
          })
      },
      retry: (failures, error: any) => {
        if (error.response.status === 404 || error.response.status === 503)
          return false
        return failures <= 3
      },
      onError(error: any) {
        if (error.response.status === 503) {
          dispatchMaintenanceMode()
        }
        if (error.response.status === 400) {
          toast.error(
            <>
              <ToastTitle>{i18next.t("error")}</ToastTitle>
              <ToastBody>{error.response.data.message}</ToastBody>
            </>,
            { duration: 12000 }
          )
        }
      },
      useErrorBoundary: (error: any) => {
        return [404, 403].includes(error.response.status)
      },
      notifyOnChangeProps: "tracked",
      // staleTime: 5 * (60 * 1000), // 5 mins
    },
  },
})

const QueryProvider: React.FC = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      {children}
    </QueryClientProvider>
  )
}

export default QueryProvider
